import {
  PlanPriceBreakdown,
  ServiceType,
} from '../features/serviceTypes/serviceTypesSlice';
import {
  firstPlanVisitFeeCents,
  MobileServiceFeeConfig,
  prepayDiscountCents,
} from './bookingPricing';
import {
  APERIO_PLAN_ORDER,
  AperioPlanKind,
  aperioPlanKind,
} from './aperioPlanCopy';
import { isSubscribeTo, pairedKey } from './serviceKeys';

/**
 * The numbers behind one of the Aperio plan cards, for one vehicle. Every
 * figure comes from the server's rows (the plan row's price and breakdown,
 * the onboarding row's price) and the tenant's fee rule, so what a card says
 * is what the booking charges.
 */
export interface AperioPlanOption {
  kind: AperioPlanKind;
  /** The visible "subscribe to" row: what the basket gets when chosen. */
  onboarding: ServiceType;
  /** The hidden plan row paired with it. */
  plan: ServiceType;
  /** The plan row's price: the year, or payment 1 of the monthly plan. */
  planCents: number;
  breakdown: PlanPriceBreakdown | null;
  /** The vehicle's price for a year of the plan (monthly: before premium). */
  annualCents: number;
  onboardingCents: number;
  /** Fee for visits inside the allowance (All-Inclusive: its own row). */
  feeCents: number;
  /**
   * Fee the plan's first visit is charged: $0 when the onboarding includes
   * it (firstVisitFeeIncluded), the next visit's fee otherwise.
   */
  nextVisitFeeCents: number;
  /** True when the onboarding includes the first visit's fee (Aperio). */
  firstVisitFeeIncluded: boolean;
  highUseFeeCents: number | null;
  visitsIncluded: number | null;
  /** Monthly: payments in the term (1 on an annual plan). */
  instalments: number;
  /** Monthly: payment 12 (equal to planCents when there is no remainder). */
  lastInstalmentCents: number;
  /** Monthly: what paying monthly adds to the year; 0 on an annual plan. */
  monthlyPremiumCents: number;
  /** What the whole term costs (annual: planCents). */
  termTotalCents: number;
  /**
   * Due at the first visit: the plan (or payment 1) + onboarding + that
   * visit's fee ($0 when the onboarding includes it). The figure the
   * schedule step and the bottom bar show once this plan is in the basket.
   */
  dueCents: number;
  /** The same, prepaid online. */
  prepaidCents: number;
}

const cents = (dollars: number | null | undefined): number => (
  Math.round((Number(dollars) || 0) * 100)
);

/**
 * The three plans (whichever of them the catalogue has), Yearly first.
 * `visible` is the step's visible subscription rows (the onboarding
 * services), `hidden` the plan rows paired with them.
 */
export function aperioPlanOptions(
  visible: ServiceType[],
  hidden: ServiceType[],
  feeConfig: MobileServiceFeeConfig,
  prepayDiscountPercent: number,
): AperioPlanOption[] {
  const options: AperioPlanOption[] = [];
  APERIO_PLAN_ORDER.forEach((kind) => {
    const onboarding = visible.find(
      (s) => isSubscribeTo(s.nameKey) && aperioPlanKind(s.nameKey) === kind,
    );
    if (!onboarding) return;
    const planKey = pairedKey(onboarding.nameKey);
    const plan = hidden.find((s) => s.nameKey === planKey);
    if (!plan) return;

    const allInclusive = kind === 'allInclusive';
    const breakdown = plan.planPriceBreakdown ?? null;
    const planCents = cents(plan.discountedValue ?? plan.value);
    const onboardingCents = cents(
      onboarding.discountedValue ?? onboarding.value,
    );
    const feeCents = allInclusive
      ? (feeConfig.mobileServiceFeeAllInclusiveCents ?? 0)
      : (feeConfig.mobileServiceFeeCents ?? 0);
    const nextVisitFeeCents = firstPlanVisitFeeCents(
      feeConfig,
      allInclusive,
    ) ?? 0;

    let instalments = 1;
    let lastInstalmentCents = planCents;
    let monthlyPremiumCents = 0;
    let termTotalCents = planCents;
    let annualCents = breakdown?.totalCents ?? planCents;
    if (kind === 'monthly') {
      instalments = breakdown?.instalmentCount ?? 12;
      lastInstalmentCents = breakdown?.lastInstalmentCents ?? planCents;
      termTotalCents = breakdown?.termTotalCents
        ?? planCents * (instalments - 1) + lastInstalmentCents;
      monthlyPremiumCents = breakdown?.monthlyPremiumCents
        ?? (breakdown ? termTotalCents - breakdown.totalCents : 0);
      annualCents = breakdown?.totalCents ?? termTotalCents;
    }

    const dueCents = planCents + onboardingCents + nextVisitFeeCents;
    options.push({
      kind,
      onboarding,
      plan,
      planCents,
      breakdown,
      annualCents,
      onboardingCents,
      feeCents,
      nextVisitFeeCents,
      firstVisitFeeIncluded: !!feeConfig.planStartMobileFeeIncluded,
      highUseFeeCents: feeConfig.mobileServiceFeeHighUseCents ?? null,
      visitsIncluded: feeConfig.mobileServiceFeeVisitsIncluded ?? null,
      instalments,
      lastInstalmentCents,
      monthlyPremiumCents,
      termTotalCents,
      dueCents,
      prepaidCents: dueCents + prepayDiscountCents(
        dueCents,
        prepayDiscountPercent,
      ),
    });
  });
  return options;
}

/**
 * What paying for the year up front saves against the monthly plan, in
 * cents (the monthly premium, $50), or null when there is no monthly plan to
 * compare with or it does not cost more.
 */
export function yearlySavingsCents(options: AperioPlanOption[]): number | null {
  const yearly = options.find((o) => o.kind === 'yearly');
  const monthly = options.find((o) => o.kind === 'monthly');
  if (!yearly || !monthly) return null;
  const saving = monthly.termTotalCents - yearly.termTotalCents;
  return saving > 0 ? saving : null;
}

/**
 * How the plan cards quote their price (owner, 2026-10): a month by default,
 * a year at the customer's choice. Only the figure on the card changes; what
 * the plan charges, and the "Due at first visit" line, do not.
 */
export type PlanPriceView = 'month' | 'year';

/** A year's price, a month: divided by 12, to the cent ($259 -> $21.58). */
export const perMonthCents = (yearCents: number): number => (
  Math.round(yearCents / 12)
);

export interface PlanCardPrice {
  /** The card's big figure, in cents, a month or a year as the view says. */
  amountCents: number;
  /**
   * Struck through beside it: on Yearly, the Monthly plan's figure in the
   * same view, while paying for the year saves something. Null otherwise.
   */
  wasCents: number | null;
}

/**
 * The figure a plan card shows in a view, from the per-vehicle prices
 * `aperioPlanOptions` computed (never a constant):
 *   - Yearly: the year's price, or that ÷ 12; struck against the Monthly
 *     plan's year total, or its monthly payment, when it saves something.
 *   - Monthly: its monthly payment, or what its payments add up to.
 *   - All-Inclusive: the year's price, or that ÷ 12.
 */
export function planCardPrice(
  option: AperioPlanOption,
  options: AperioPlanOption[],
  view: PlanPriceView,
): PlanCardPrice {
  if (option.kind === 'monthly') {
    return {
      amountCents: view === 'month' ? option.planCents : option.termTotalCents,
      wasCents: null,
    };
  }
  const amountCents = view === 'month'
    ? perMonthCents(option.planCents)
    : option.planCents;
  const monthly = options.find((o) => o.kind === 'monthly');
  let wasCents: number | null = null;
  if (option.kind === 'yearly' && monthly && yearlySavingsCents(options)) {
    const compare = view === 'month'
      ? monthly.planCents
      : monthly.termTotalCents;
    if (compare > amountCents) wasCents = compare;
  }
  return { amountCents, wasCents };
}
